/**
 * Forward-Scatter Obstruction Detection
 *
 * In a static environment, the same reflectors (walls, furniture) produce
 * roughly the same range-profile peaks each scan. By capturing a baseline
 * during clean-scene calibration and comparing each new scan against it, we
 * can flag bins where a previously-strong reflector got weaker — that
 * suggests something is now blocking the path to that reflector.
 *
 * Pure functions, no side effects. Use Float32Array for signal data.
 */

export interface ForwardScatterBaseline {
  /** Median of profiles per bin. */
  reference: Float32Array;
  /** MAD per bin (median absolute deviation), as a per-bin noise estimate. */
  noiseFloor: Float32Array;
  /** Number of profiles that contributed to this baseline. */
  pingCount: number;
}

export interface ObstructionEvent {
  binIndex: number;
  /** Meters; computed from binIndex + minRange/maxRange. */
  range: number;
  /** baseline.reference[bin] */
  baselineStrength: number;
  /** profile[bin] */
  currentStrength: number;
  /** 1 - current/baseline, clamped to [0, 1]. */
  dropRatio: number;
}

export interface ForwardScatterResult {
  /** Per-bin drop magnitude (clamped >= 0); same length as profile. */
  obstructionMap: Float32Array;
  /** Sorted by dropRatio desc; only bins meaningfully strong in baseline. */
  obstructions: ObstructionEvent[];
  /** Sum of obstructionMap (overall obstruction "amount"). */
  totalScore: number;
  /** false if no baseline was supplied. */
  hasBaseline: boolean;
}

/**
 * Returns the median of `values`. Mutates the input by sorting it; pass a
 * scratch copy when that is undesirable. Empty input returns 0.
 */
function median(values: number[]): number {
  const n = values.length;
  if (n === 0) return 0;
  values.sort((a, b) => a - b);
  const mid = n >> 1;
  if ((n & 1) === 1) return values[mid];
  return 0.5 * (values[mid - 1] + values[mid]);
}

/**
 * Build a forward-scatter baseline from a list of clean-scene range profiles.
 *
 * Uses per-bin median (robust to single bad pings) and per-bin MAD as a
 * noise estimate. Returns null if `profiles` is empty, any profile has a
 * length different from `heatBins`, or `profiles.length < minPings`.
 *
 * `minPings` (default 3) guards against the degenerate case where MAD ≈ 0
 * because too few pings contributed; with MAD=0 the strong-reflector ratio
 * filter inside `computeForwardScatter` produces meaningless detections.
 */
export function buildForwardScatterBaseline(args: {
  profiles: Float32Array[];
  heatBins: number;
  /** Minimum number of profiles required. Default 3 — fewer than this and MAD is unreliable. */
  minPings?: number;
}): ForwardScatterBaseline | null {
  const { profiles, heatBins, minPings = 3 } = args;
  if (profiles.length === 0) return null;
  if (profiles.length < minPings) return null;
  if (heatBins <= 0) return null;
  for (const p of profiles) {
    if (p.length !== heatBins) return null;
  }

  const reference = new Float32Array(heatBins);
  const noiseFloor = new Float32Array(heatBins);
  const colBuf: number[] = new Array(profiles.length);
  const devBuf: number[] = new Array(profiles.length);

  for (let bin = 0; bin < heatBins; bin++) {
    for (let i = 0; i < profiles.length; i++) {
      colBuf[i] = profiles[i][bin];
    }
    // copy because median() sorts in place
    const colCopy = colBuf.slice();
    const med = median(colCopy);
    reference[bin] = med;

    for (let i = 0; i < profiles.length; i++) {
      devBuf[i] = Math.abs(colBuf[i] - med);
    }
    const devCopy = devBuf.slice();
    noiseFloor[bin] = median(devCopy);
  }

  return {
    reference,
    noiseFloor,
    pingCount: profiles.length,
  };
}

/**
 * Compare a new range profile against a forward-scatter baseline. Bins where
 * a previously-strong reflector dropped significantly get reported as
 * potential obstruction events.
 */
export function computeForwardScatter(args: {
  profile: Float32Array;
  baseline: ForwardScatterBaseline | null;
  minRange: number;
  maxRange: number;
  strongReflectorThreshold?: number;
  obstructionThreshold?: number;
}): ForwardScatterResult {
  const {
    profile,
    baseline,
    minRange,
    maxRange,
    strongReflectorThreshold = 0.3,
    obstructionThreshold = 0.4,
  } = args;

  if (baseline === null) {
    return {
      obstructionMap: new Float32Array(profile.length),
      obstructions: [],
      totalScore: 0,
      hasBaseline: false,
    };
  }

  const len = Math.min(profile.length, baseline.reference.length);
  const obstructionMap = new Float32Array(profile.length);

  // Find peak in baseline reference for the strong-reflector threshold.
  let refMax = 0;
  for (let i = 0; i < len; i++) {
    const v = baseline.reference[i];
    if (v > refMax) refMax = v;
  }

  let totalScore = 0;
  for (let i = 0; i < len; i++) {
    const drop = baseline.reference[i] - profile[i];
    const clamped = drop > 0 ? drop : 0;
    obstructionMap[i] = clamped;
    totalScore += clamped;
  }

  const obstructions: ObstructionEvent[] = [];
  const minStrong = strongReflectorThreshold * refMax;
  const heatBins = baseline.reference.length;
  const span = maxRange - minRange;
  const denom = heatBins > 1 ? heatBins - 1 : 1;

  for (let i = 0; i < len; i++) {
    const ref = baseline.reference[i];
    const noise = baseline.noiseFloor[i];
    if (ref <= minStrong) continue;
    if (ref <= 2 * noise) continue;
    const drop = obstructionMap[i];
    const ratio = drop / ref;
    if (ratio < obstructionThreshold) continue;
    const clampedRatio = ratio > 1 ? 1 : ratio < 0 ? 0 : ratio;
    const range = minRange + (i / denom) * span;
    obstructions.push({
      binIndex: i,
      range,
      baselineStrength: ref,
      currentStrength: profile[i],
      dropRatio: clampedRatio,
    });
  }

  obstructions.sort((a, b) => b.dropRatio - a.dropRatio);

  return {
    obstructionMap,
    obstructions,
    totalScore,
    hasBaseline: true,
  };
}
